/**
 * Fechas del portal con la zona IANA que decide el servidor (la propia de la persona o la de la empresa), nunca la del
 * navegador. Intl formatea; date-fns queda para los cálculos.
 */
export type DateStyle = "date" | "time" | "dateTime" | "dayMonth";

const options: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  date: { day: "2-digit", month: "2-digit", year: "numeric" },
  time: { hour: "2-digit", minute: "2-digit", hour12: false },
  dateTime: { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false },
  dayMonth: { day: "numeric", month: "short" },
};

const cache = new Map<string, Intl.DateTimeFormat>();

/** Formatea un instante en la zona indicada. Sin zona válida no adivina: usa UTC y lo deja ver. */
export function formatInZone(
  value: Date | string,
  timeZone: string | null | undefined,
  locale: string,
  style: DateStyle,
): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const zone = timeZone && isTimeZone(timeZone) ? timeZone : "UTC";
  const key = `${locale}|${zone}|${style}`;
  let format = cache.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, { ...options[style], timeZone: zone });
    cache.set(key, format);
  }
  return format.format(date);
}

/** Día calendario (aaaa-mm-dd) del instante en la zona: «hoy» y «ayer» se comparan con esto. */
export function dayInZone(value: Date | string, timeZone: string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Zona IANA válida para Intl. */
export function isTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Formatos cortos de .NET ("d" y "g") como los usa hoy el portal en Blazor: la fecha y hora del valor en el desfase con
 * que viene del servidor (no en la zona del navegador). es-PE: 24/10/2026 y 24/10/2026 5:12; en-US: 10/24/2026 y
 * 10/24/2026 5:12 AM.
 */
export function dotnetShort(iso: string, locale: string, pattern: "d" | "g"): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(iso);
  if (!match) return iso;
  const [, year, month, day, hour = "00", minute = "00"] = match;
  if (locale === "en-US") {
    const date = `${Number(month)}/${Number(day)}/${year}`;
    if (pattern === "d") return date;
    const h = Number(hour);
    return `${date} ${h % 12 === 0 ? 12 : h % 12}:${minute} ${h < 12 ? "AM" : "PM"}`;
  }
  const date = `${day}/${month}/${year}`;
  return pattern === "d" ? date : `${date} ${Number(hour)}:${minute}`;
}
